import React, { useState, useEffect, useCallback } from "react";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { getUserFormDetails } from "../../api/getUserFormDetailsApi";
import { Role } from "../../api/types";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

interface UserListFiltersProps {
  filters: {
    firstName?: string;
    lastName?: string;
    email?: string;
    businessRoleId?: number;
  };
  onFilterChange: (
    filterKey: string,
    value: string | number | undefined,
  ) => void;
}

const UserListFilters: React.FC<UserListFiltersProps> = ({
  filters,
  onFilterChange,
}) => {
  const [firstNameFilter, setFirstNameFilter] = useState(
    filters.firstName || "",
  );
  const [lastNameFilter, setLastNameFilter] = useState(filters.lastName || "");
  const [emailFilter, setEmailFilter] = useState(filters.email || "");
  const [roleFilter, setRoleFilter] = useState(
    filters.businessRoleId?.toString() || "",
  );
  const [roles, setRoles] = useState<Role[]>([]);
  const [loadingRoles, setLoadingRoles] = useState(false);

  const debouncedFirstNameFilter = useDebouncedValue(firstNameFilter, 500);
  const debouncedLastNameFilter = useDebouncedValue(lastNameFilter, 500);
  const debouncedEmailFilter = useDebouncedValue(emailFilter, 500);

  // Fetch roles for dropdown
  useEffect(() => {
    const loadRoles = async () => {
      setLoadingRoles(true);
      try {
        const response = await getUserFormDetails();
        setRoles(response.data.availableRoles);
      } catch (error) {
        console.error("Failed to fetch roles:", error);
      } finally {
        setLoadingRoles(false);
      }
    };
    loadRoles();
  }, []);

  useEffect(() => {
    onFilterChange("firstName", debouncedFirstNameFilter);
  }, [debouncedFirstNameFilter]);

  useEffect(() => {
    onFilterChange("lastName", debouncedLastNameFilter);
  }, [debouncedLastNameFilter]);

  useEffect(() => {
    onFilterChange("email", debouncedEmailFilter);
  }, [debouncedEmailFilter]);

  const handleRoleChange = (value: string) => {
    if (value === "all") {
      setRoleFilter("");
      onFilterChange("businessRoleId", undefined);
    } else {
      setRoleFilter(value);
      onFilterChange("businessRoleId", parseInt(value, 10));
    }
  };

  const handleClearAllFilters = useCallback(() => {
    setFirstNameFilter("");
    setLastNameFilter("");
    setEmailFilter("");
    setRoleFilter("");
    onFilterChange("firstName", undefined);
    onFilterChange("lastName", undefined);
    onFilterChange("email", undefined);
    onFilterChange("businessRoleId", undefined);
  }, [onFilterChange]);

  const hasActiveFilters =
    firstNameFilter || lastNameFilter || emailFilter || roleFilter;

  return (
    <div className="mb-6">
      <div className="flex gap-4 items-end flex-wrap">
        {/* Role Dropdown */}
        <div className="min-w-[160px]">
          <Label htmlFor="role">Role</Label>
          <Select
            disabled={loadingRoles}
            onValueChange={handleRoleChange}
            value={roleFilter || "all"}
          >
            <SelectTrigger className="mt-1.5 bg-white" id="role">
              <SelectValue
                placeholder={loadingRoles ? "Loading..." : "All Roles"}
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              {roles.map((role) => (
                <SelectItem key={role.id} value={role.id.toString()}>
                  {role.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* First Name */}
        <div className="flex-1 min-w-[140px]">
          <Label htmlFor="firstName">First Name</Label>
          <Input
            className="mt-1.5 bg-white"
            id="firstName"
            onChange={(e) => setFirstNameFilter(e.target.value)}
            placeholder="Search..."
            type="text"
            value={firstNameFilter}
          />
        </div>

        {/* Last Name */}
        <div className="flex-1 min-w-[140px]">
          <Label htmlFor="lastName">Last Name</Label>
          <Input
            className="mt-1.5 bg-white"
            id="lastName"
            onChange={(e) => setLastNameFilter(e.target.value)}
            placeholder="Search..."
            type="text"
            value={lastNameFilter}
          />
        </div>

        {/* Email */}
        <div className="flex-1 min-w-[180px]">
          <Label htmlFor="email">Email</Label>
          <Input
            className="mt-1.5 bg-white"
            id="email"
            onChange={(e) => setEmailFilter(e.target.value)}
            placeholder="Search..."
            type="text"
            value={emailFilter}
          />
        </div>

        {/* Clear All Filters */}
        <div className="flex items-end">
          <Button
            disabled={!hasActiveFilters}
            onClick={handleClearAllFilters}
            variant="outline"
          >
            Clear Filters
          </Button>
        </div>
      </div>
    </div>
  );
};

export default UserListFilters;
